import { c as _c } from "react/compiler-runtime";
import { XCircleIcon } from '@systemeio/icons';
import type { PropsWithChildren, ReactNode } from 'react';
import { ActionMenu, Button, IconTile, SafeHtmlText, type ActionMenuItemProps } from 'shared/components';
import { CompactTime, RelativeTime } from 'shared/components/relative-time';
import { ViewportSwitch } from 'shared/components/viewport-switch';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { twJoin } from 'tailwind-merge';
export type NotificationItemVariant = 'popover' | 'list';
export interface NotificationItemViewProps {
  variant?: NotificationItemVariant;
  isRead?: boolean;
  icon?: ReactNode;
  message: string;
  /** What the item type adds under the message: a progress bar, a description, its own details. */
  children?: ReactNode;
  time?: string;
  /** Controls the item type puts beside the row menu, such as cancelling a running job. */
  controls?: ReactNode;
  actions?: ActionMenuItemProps[];
  onClick?: () => void;
}
export const NotificationItemView = t0 => {
  const $ = _c(40);
  const {
    variant: t1,
    isRead,
    icon,
    message,
    children,
    time,
    controls,
    actions,
    onClick
  } = t0;
  const variant = t1 === undefined ? "popover" : t1;
  const {
    t
  } = useLocoTranslation();
  const isPopover = variant === "popover";
  const hasActions = Boolean(actions?.length);
  let t2;
  let t3;
  if ($[0] !== children || $[1] !== icon || $[2] !== isPopover || $[3] !== isRead || $[4] !== message || $[5] !== onClick || $[6] !== time) {
    const contentClassName = twJoin("flex min-w-0 flex-1 items-center gap-3 text-start", isPopover ? "rounded-md p-2.5" : "tablet:px-6 desktop:py-2.5 p-4");
    let t4;
    if ($[9] !== icon) {
      t4 = icon && <IconTile size="s" tone="gray">{icon}</IconTile>;
      $[9] = icon;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== message) {
      t5 = <SafeHtmlText className="break-words [&_b]:font-medium [&_strong]:font-medium" html={message} />;
      $[11] = message;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== time) {
      t6 = time && <ViewportSwitch at="desktop" below={<CompactTime time={time} />} from={<span className="text-text-tertiary"><RelativeTime time={time} /></span>} />;
      $[13] = time;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== children || $[16] !== t5 || $[17] !== t6) {
      t7 = <span className="flex max-w-200 min-w-0 flex-1 flex-col gap-1">{t5}{children}{t6}</span>;
      $[15] = children;
      $[16] = t5;
      $[17] = t6;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== t4 || $[20] !== t7) {
      t8 = <span className="flex min-w-0 flex-1 items-start gap-2">{t4}{t7}</span>;
      $[19] = t4;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== isRead) {
      t9 = !isRead && <NotificationUnreadDot />;
      $[22] = isRead;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== t8 || $[25] !== t9) {
      t10 = <>{t8}{t9}</>;
      $[24] = t8;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    const content = t10;
    const t11 = isPopover && "rounded-md";
    const t12 = onClick && "transition-base hover:bg-bg-secondary transition-colors";
    if ($[27] !== t11 || $[28] !== t12) {
      t2 = twJoin("flex w-full items-center", t11, t12);
      $[27] = t11;
      $[28] = t12;
      $[29] = t2;
    } else {
      t2 = $[29];
    }
    t3 = onClick ? <button className={twJoin(contentClassName, "focus-visible:focus-ring-primary outline-none")} onClick={onClick} type="button">{content}</button> : <div className={contentClassName}>{content}</div>;
    $[0] = children;
    $[1] = icon;
    $[2] = isPopover;
    $[3] = isRead;
    $[4] = message;
    $[5] = onClick;
    $[6] = time;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[30] !== actions || $[31] !== controls || $[32] !== hasActions || $[33] !== isPopover || $[34] !== t) {
    t4 = (controls || hasActions) && <div className={twJoin("flex shrink-0 items-center gap-3", isPopover ? "pr-2.5" : "tablet:pr-6 pr-4")}>{controls}{hasActions && actions && <ActionMenu ariaLabel={t("global.more_actions")} menuItems={actions.map(_temp)} />}</div>;
    $[30] = actions;
    $[31] = controls;
    $[32] = hasActions;
    $[33] = isPopover;
    $[34] = t;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  let t5;
  if ($[36] !== t2 || $[37] !== t3 || $[38] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[36] = t2;
    $[37] = t3;
    $[38] = t4;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  return t5;
};
export const NotificationUnreadDot = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span aria-hidden={true} className="bg-bg-accent-active size-1.5 shrink-0 rounded-full" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const NotificationItemDescription = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="text-text-quaternary">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface NotificationItemCancelButtonProps {
  isLoading?: boolean;
  onClick: () => void;
}
export const NotificationItemCancelButton = t0 => {
  const $ = _c(7);
  const {
    isLoading,
    onClick
  } = t0;
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("global.cancel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <XCircleIcon />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isLoading || $[4] !== onClick || $[5] !== t1) {
    t3 = <Button aria-label={t1} className="rounded-full" isLoading={isLoading} onClick={onClick} size="icon-sm" variant="ghost-gray">{t2}</Button>;
    $[3] = isLoading;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
function _temp(action, index) {
  return {
    ...action,
    id: `notification-action-${index}`
  };
}